import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Rol } from '../../api/operaciones';
import type { Sede, TransferenciaPendiente } from '../../api/ops/equipo';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Chip } from '../../componentes/Chip';
import { Icono } from '../../componentes/Icono';
import { Segmentos, type Segmento } from '../../componentes/Segmentos';
import { useCabecera, type AccionCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import { rutaConApartado, type PropsPantalla } from '../tipos';
import { PERMISOS, type ContextoEquipo } from './contexto';
import { Invitaciones, PanelInvitar } from './Invitaciones';
import { Miembros } from './Miembros';
import { Roles } from './Roles';
import { Tablets } from './Tablets';
import { AYUDA, esNoContratado, fechaLarga, nombreDePila, textoDeError, type Apartado } from './textos';
import './equipo.css';

/**
 * Equipo (1.9): apartados Miembros · Invitaciones · Roles (1.11) · Tablets (1.13), con la cifra de cada uno
 * (`total_count`, G139) y `?apartado=`. Arriba, la transferencia de la propiedad pendiente (1.12, `getBusiness`): el
 * propietario la ve con «Cancelar» y quien la recibe, con «Ver la propuesta», que lleva a `/propiedad`. Invitaciones y
 * Tablets solo salen con sus permisos (`equipo.miembros.gestionar`, `equipo.tablets.gestionar`).
 */
const SEGMENTOS: Record<Apartado, Omit<Segmento, 'cifra'>> = {
    miembros: { etiqueta: 'Miembros', icono: 'users' },
    invitaciones: { etiqueta: 'Invitaciones', corta: 'Invitac.', icono: 'mail' },
    roles: { etiqueta: 'Roles', icono: 'user-cog' },
    tablets: { etiqueta: 'Tablets', icono: 'tablet-smartphone' },
};

export function Equipo(props: PropsPantalla) {
    const { ops, negocio, busqueda, ruta, navegar, movil, soloLectura, membresia, nombreNegocio } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const gestionar = permisos.puede(PERMISOS.gestionar);
    const verTablets = permisos.puede(PERMISOS.tablets);
    const apartados = useMemo<Apartado[]>(() => ['miembros', ...(gestionar ? (['invitaciones'] as const) : []), 'roles', ...(verTablets ? (['tablets'] as const) : [])], [gestionar, verTablets]);
    const pedido = busqueda.get('apartado') as Apartado | null;
    const apartado: Apartado = pedido !== null && apartados.includes(pedido) ? pedido : 'miembros';

    const [version, setVersion] = useState(0);
    const [cifras, setCifras] = useState<Partial<Record<Apartado, number>>>({});
    const [roles, setRoles] = useState<Rol[]>([]);
    const [sedes, setSedes] = useState<Sede[]>([]);
    const [transferencia, setTransferencia] = useState<TransferenciaPendiente | null>(null);
    const [zonaHoraria, setZonaHoraria] = useState<string | null>(null);
    const [tabletsNo, setTabletsNo] = useState(false);
    const [invitar, setInvitar] = useState(false);
    const [crearRol, setCrearRol] = useState(false);
    const [emparejar, setEmparejar] = useState(false);

    const refrescar = useCallback(() => {
        setVersion((v) => v + 1);
    }, []);
    const cifraMiembros = useCallback((n: number) => {
        setCifras((c) => (c.miembros === n ? c : { ...c, miembros: n }));
    }, []);
    const cifraInvitaciones = useCallback((n: number) => {
        setCifras((c) => (c.invitaciones === n ? c : { ...c, invitaciones: n }));
    }, []);
    const cifraTablets = useCallback((n: number) => {
        setCifras((c) => (c.tablets === n ? c : { ...c, tablets: n }));
    }, []);
    const tabletsNoContratadas = useCallback(() => {
        setTabletsNo(true);
    }, []);
    const puedeVerSedes = permisos.puede(PERMISOS.negocio);

    // Cifras de las pestañas (la del apartado abierto la pone también su lista), roles, sedes y transferencia pendiente.
    useEffect(() => {
        let vivo = true;
        const si = (f: () => void) => {
            if (vivo) {
                f();
            }
        };
        ops.listMembers(negocio, { limit: 1 })
            .then((l) => {
                si(() => {
                    cifraMiembros(l.total_count);
                });
            })
            .catch(() => undefined);
        if (gestionar) {
            ops.listInvitations(negocio, { limit: 1 })
                .then((l) => {
                    si(() => {
                        cifraInvitaciones(l.total_count);
                    });
                })
                .catch(() => undefined);
        }
        if (verTablets) {
            ops.listDevices(negocio, { limit: 1 })
                .then((l) => {
                    si(() => {
                        cifraTablets(l.total_count);
                    });
                })
                .catch((e: unknown) => {
                    if (esNoContratado(e)) {
                        si(tabletsNoContratadas);
                    }
                });
        }
        ops.listRoles(negocio)
            .then((l) => {
                si(() => {
                    setRoles(l.data);
                    setCifras((c) => ({ ...c, roles: l.total_count }));
                });
            })
            .catch(() => undefined);
        if (puedeVerSedes) {
            ops.listLocations(negocio)
                .then((l) => {
                    si(() => {
                        setSedes(l.data.filter((s) => s.status === 'active'));
                    });
                })
                .catch(() => undefined);
        }
        ops.getBusiness(negocio)
            .then((n) => {
                si(() => {
                    setTransferencia(n.pending_ownership_transfer ?? null);
                    setZonaHoraria(n.timezone);
                });
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, version, gestionar, verTablets, puedeVerSedes, cifraMiembros, cifraInvitaciones, cifraTablets, tabletsNoContratadas]);

    const c: ContextoEquipo = {
        ops,
        negocio,
        nombreNegocio,
        movil,
        soloLectura,
        yo: membresia?.member_id ?? null,
        soyPropietario: membresia?.is_owner === true,
        permisos,
        avisar,
        roles,
        sedes,
        transferencia,
        zonaHoraria,
        refrescar,
        recargarMarco: props.recargar,
    };

    const acciones: AccionCabecera[] = [];
    if ((apartado === 'miembros' || apartado === 'invitaciones') && gestionar) {
        acciones.push({ clave: 'invitar', texto: 'Invitar', icono: 'user-plus', desactivado: soloLectura, onClick: () => { setInvitar(true); } });
    }
    if (apartado === 'roles' && permisos.puede(PERMISOS.roles)) {
        acciones.push({ clave: 'rol', texto: 'Crear rol', icono: 'plus', desactivado: soloLectura, onClick: () => { setCrearRol(true); } });
    }
    if (apartado === 'tablets' && verTablets && !tabletsNo) {
        acciones.push({ clave: 'tablet', texto: 'Emparejar tablet', icono: 'plus', desactivado: soloLectura, onClick: () => { setEmparejar(true); } });
    }
    useCabecera({ ayuda: AYUDA[apartado], acciones });

    const cancelarTransferencia = () => {
        if (transferencia === null) {
            return;
        }
        ops.cancelOwnershipTransfer(negocio, transferencia.id)
            .then(() => {
                avisar({ tipo: 'ok', texto: 'Transferencia cancelada. Sigues siendo el propietario.' });
                refrescar();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido cancelar la transferencia. Prueba de nuevo.') });
            });
    };

    const soyDestinataria = transferencia !== null && membresia !== undefined && transferencia.to_member_id === membresia.member_id;
    const soyPropietario = membresia?.is_owner === true;

    return (
        <div className="rv-equipo">
            <div className="rv-eq-barra">
                <Segmentos
                    etiqueta="Apartados de equipo"
                    movil={movil}
                    items={apartados.map((a) => ({ ...SEGMENTOS[a], cifra: cifras[a] ?? null }))}
                    valor={apartados.indexOf(apartado)}
                    onCambio={(i) => {
                        const a = apartados[i];
                        if (a !== undefined) {
                            navegar(rutaConApartado(ruta, a === 'miembros' ? null : a));
                        }
                    }}
                />
            </div>

            {transferencia !== null && (soyPropietario || soyDestinataria) && (
                <div className="rv-eq-transferencia" role="status">
                    <span className="rv-eq-transferencia__baldosa">
                        <Icono nombre="crown" />
                    </span>
                    <span className="rv-eq-transferencia__texto">
                        <strong>
                            {soyPropietario ? `Transferencia a ${transferencia.to_member_name}` : `${transferencia.from_member_name ?? ''} te ofrece la propiedad`}
                            <Chip estado="pending">Pendiente de aceptar</Chip>
                        </strong>
                        <span>
                            {soyPropietario
                                ? `${nombreDePila(transferencia.to_member_name)} tiene hasta ${fechaLarga(transferencia.expires_at)} para aceptarla desde el aviso o el email. Hasta entonces sigues siendo el propietario.`
                                : `Tienes hasta ${fechaLarga(transferencia.expires_at)} para aceptarla o rechazarla.`}
                        </span>
                    </span>
                    {soyPropietario && permisos.puede(PERMISOS.transferir) && (
                        <Boton tipo="secundario" icono="x" disabled={soloLectura} onClick={cancelarTransferencia}>
                            Cancelar
                        </Boton>
                    )}
                    {soyDestinataria && (
                        <Boton icono="arrow-right" iconoDetras onClick={() => { navegar('/propiedad'); }}>
                            Ver la propuesta
                        </Boton>
                    )}
                </div>
            )}

            {apartado === 'miembros' && <Miembros c={c} cifra={cifraMiembros} />}
            {apartado === 'invitaciones' && <Invitaciones c={c} cifra={cifraInvitaciones} version={version} />}
            {apartado === 'roles' && (
                <Roles
                    c={c}
                    crear={crearRol}
                    onCerrarCrear={() => {
                        setCrearRol(false);
                    }}
                />
            )}
            {apartado === 'tablets' && (
                <Tablets
                    c={c}
                    cifra={cifraTablets}
                    emparejar={emparejar}
                    onCerrarEmparejar={() => {
                        setEmparejar(false);
                    }}
                    onNoContratado={tabletsNoContratadas}
                />
            )}

            <PanelInvitar
                c={c}
                abierto={invitar}
                onCerrar={() => {
                    setInvitar(false);
                }}
                onEnviada={() => {
                    setInvitar(false);
                    refrescar();
                }}
            />
        </div>
    );
}
